import {
  FloatingFocusManager,
  FloatingPortal,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react'
import {type OptionValue, cn} from '@tempmee/core'
import {OutlineChevronDownIcon} from '@tempmee/react-icons'
import {type KeyboardEvent, type ReactNode, forwardRef, useRef, useState} from 'react'
import {Center} from '../box'
import {CircularProgress} from '../circular-progress'
import {MenuItemVariantsFactory} from '../menu/Variants'

export interface InputSelectFieldProps<T = string> {
  /** List of selectable options */
  options: OptionValue<T>[]
  /** Currently selected value */
  value?: T
  /** Callback when an option is selected */
  onChange?: (value: T) => void
  /** Callback fired when the dropdown closes */
  onClose?: () => void
  /**
   * Floating label rendered inside the field; doubles as the placeholder when
   * empty (MUI filled style). When omitted, `placeholder` text is shown instead.
   */
  label?: string
  /** Placeholder text shown when no value is selected and no `label` is set */
  placeholder?: string
  /** Icon rendered on the left side of the trigger */
  leadingIcon?: ReactNode
  /** Applies error styling (pink fill + red underline). Mirrors MUI's `Select`/`TextField` `error`. */
  error?: boolean
  /**
   * @deprecated Use `error` instead (renamed in v3 to match MUI + `TextField`).
   * Still honored for back-compat.
   */
  isError?: boolean
  /** Disables the trigger */
  disabled?: boolean
  /** Shows a spinner inside the dropdown while options are loading */
  isLoading?: boolean
  /** Additional className for the trigger container */
  className?: string
}

function InputSelectFieldInner<T = string>(
  {
    className,
    options,
    value,
    onChange,
    onClose,
    label,
    placeholder = 'Select...',
    leadingIcon,
    error,
    isError = false,
    disabled = false,
    isLoading = false,
  }: InputSelectFieldProps<T>,
  ref: React.Ref<HTMLButtonElement>,
) {
  // `error` is the v3 (MUI-aligned) name; `isError` is the deprecated alias.
  const hasError = error ?? isError
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const listRef = useRef<(HTMLElement | null)[]>([])

  const selectedOption = options.find((o) => o.value === value)

  // Filled-field floating label: floats up when open or a value is selected.
  const shrink = isOpen || selectedOption != null

  const {refs, floatingStyles, context} = useFloating<HTMLButtonElement>({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open)
      if (!open) onClose?.()
    },
    placement: 'bottom-start',
    middleware: [
      offset(0),
      flip({padding: 8}),
      size({
        apply({rects, elements}) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
        padding: 8,
      }),
    ],
  })

  const click = useClick(context, {enabled: !disabled})
  const dismiss = useDismiss(context)
  const role = useRole(context, {role: 'listbox'})
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedOption ? options.indexOf(selectedOption) : null,
    onNavigate: setActiveIndex,
    enabled: !isLoading,
  })

  const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
    click,
    dismiss,
    role,
    listNav,
  ])

  const {isMounted, styles: transitionStyles} = useTransitionStyles(context, {duration: 150})

  const handleSelect = (optionValue: T) => {
    onChange?.(optionValue)
    setIsOpen(false)
    onClose?.()
  }

  const handleKeyDown = (e: KeyboardEvent, optionValue: T) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleSelect(optionValue)
    }
  }

  const labelColor = disabled
    ? 'text-text-disabled'
    : shrink
      ? 'text-text-primary'
      : 'text-text-secondary'

  return (
    <>
      <button
        ref={(node) => {
          refs.setReference(node)
          if (typeof ref === 'function') ref(node)
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
        }}
        type="button"
        disabled={disabled}
        aria-expanded={isOpen}
        className={cn(
          'relative flex w-full items-center gap-1 rounded-t-[4px] border-b border-solid px-3 py-2 text-left transition-colors',
          hasError
            ? 'border-error bg-[rgba(255,128,140,0.16)]'
            : 'bg-[#f6f9f8] border-divider focus-visible:border-primary',
          isOpen && !hasError && 'border-primary',
          disabled && 'cursor-not-allowed',
          'focus-visible:outline-none',
          className,
        )}
        {...getReferenceProps()}
      >
        {leadingIcon && (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center size-6 text-text-secondary [&_svg]:size-6',
              disabled && 'text-text-disabled',
            )}
          >
            {leadingIcon}
          </span>
        )}

        <span className="relative flex h-10 flex-1 items-center">
          {label && (
            <span
              className={cn(
                'pointer-events-none absolute left-0 origin-left tracking-[0.15px] transition-all duration-150 ease-out',
                shrink
                  ? 'top-0 text-[12px] font-normal leading-4'
                  : '-translate-y-1/2 top-1/2 text-base font-semibold leading-6',
                labelColor,
              )}
            >
              {label}
            </span>
          )}
          <span
            className={cn(
              'block w-full truncate text-base font-semibold leading-6',
              label && 'absolute inset-0 h-full pt-4',
              disabled
                ? 'text-text-disabled'
                : selectedOption
                  ? 'text-text-primary'
                  : 'text-text-disabled',
            )}
          >
            {selectedOption?.label ?? (label ? '' : placeholder)}
          </span>
        </span>

        <OutlineChevronDownIcon
          className={cn(
            'size-6 shrink-0 ml-1 transition-transform duration-150',
            disabled ? 'text-text-disabled' : 'text-text-secondary',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={{...floatingStyles, ...transitionStyles}}
              className="ds-scroll-area z-50 max-h-60 overflow-y-auto rounded-b-lg bg-background p-1 shadow-elevation-1 outline-none"
              {...getFloatingProps()}
            >
              {isLoading ? (
                <Center py={4}>
                  <CircularProgress size={24} />
                </Center>
              ) : (
                options.map((option, index) => {
                  const isSelected = option.value === value
                  return (
                    <div
                      key={String(option.value)}
                      ref={(node) => {
                        listRef.current[index] = node
                      }}
                      // biome-ignore lint/a11y/useSemanticElements: role="option" is required here; native <option> is only valid inside <select>/<datalist>
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={activeIndex === index ? 0 : -1}
                      // Reuses the DS MenuItem visual language (the `_MenuItem` atom the Figma
                      // references): plain text rows, light `action.hover`, teal-pill `selected`.
                      className={cn(
                        MenuItemVariantsFactory({selected: isSelected}),
                        'select-none',
                        activeIndex === index && !isSelected && 'bg-action-hover',
                      )}
                      onKeyDown={(e) => handleKeyDown(e, option.value)}
                      {...getItemProps({
                        onClick: () => handleSelect(option.value),
                      })}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                    </div>
                  )
                })
              )}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  )
}

export const InputSelectField = forwardRef(InputSelectFieldInner) as <T = string>(
  props: InputSelectFieldProps<T> & {ref?: React.Ref<HTMLButtonElement>},
) => React.ReactElement
